// @flow
import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Row, Col, Modal, Icon, message, Upload, Radio, Input,
} from 'antd';
import {
  SpH5, SpButton, SpForm, SpText, SpError,
} from 'components/DesignKit';
import { API_END_POINTS, axiosAuthInstance, axiosEmptyInstance } from 'api';

import SimpleReactValidator from 'simple-react-validator';
import { NOTIFICATION } from 'store/actionTypes';
import { MESSAGES } from 'appconstants';
import { notificationActions } from 'store/actions';
import File from '../../../../../imgs/file.svg';

type Props = {
  close: Function,
  visible: boolean,
  setNotification: Function,
  selectedAccount: Object,
};

const BulkUploadItems = (props: Props) => {
  const {
    visible, setNotification, close, selectedAccount,
  } = props;
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadType, setUploadType] = useState('import');
  const [addRecipient, setAddRecipient] = useState([]);
  const [, forceUpdate] = useState();

  const simpleValidator = useRef(new SimpleReactValidator({
    validators: {
      checkLowerCase: {
        message: 'Email address must not contain uppercase letters.',
        rule: (val) => val === val.toLowerCase(),
      },
    },
  }));

  const addNewRecipient = (event) => {
    event.preventDefault();
    setAddRecipient([...addRecipient, '']);
  };
  const handleRemoveEmail = (i) => {
    simpleValidator.current.purgeFields();
    const list = [...addRecipient];
    list.splice(i, 1);
    setAddRecipient(list);
    const hasNoEmails = (list.length === 0);
    if (hasNoEmails) {
      simpleValidator.current.purgeFields();
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data: { entity: { fileUrl } } } = await axiosAuthInstance
        .get(`${API_END_POINTS.ITEM_BULK_UPLOAD}`);
      window.open(fileUrl);
    } catch (e) {
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: MESSAGES.REPORT.DOWNLOAD_ERROR,
      });
    }
  };

  const fileReader = async file => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsArrayBuffer(file);
  });
  const beforeUpload = (file) => {
    setUploadLoading(true);
    const isLt2M = file.size / 1024 / 1024 < 2;
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/vnd.ms-excel',
    ];

    const isValidType = allowedTypes.includes(file.type);
    if (!isValidType) {
      message.error('Please selecte valid file');
      setUploadLoading(false);
      return false;
    }
    if (isValidType && !isLt2M) {
      message.error('File must smaller than 2MB!');
      setUploadLoading(false);
      return false;
    }
    setUploadLoading(false);
    return true;
  };

  const fileUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setUploadLoading(true);
      return;
    }
    try {
      setUploadLoading(true);
      const file = info.file.originFileObj;
      if (file) {
        setUploadedFile(file);
        setUploadLoading(false);
        setUploadSuccess(true);
      }
    } catch (e) {
      setUploadSuccess(false);
      setUploadedFile(null);
    } finally {
      setUploadLoading(false);
    }
  };

  let multi = false;
  const storeFile = async () => {
    const formValid = simpleValidator.current.allValid();
    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }
    if (uploadType === 'export') {
      try {
        setLoading(true);
        // eslint-disable-next-line max-len
        const { data } = await axiosAuthInstance.post(`${API_END_POINTS.BULK_ITEMS_EXPORT}`, { recipientEmails: addRecipient?.length > 0 ? addRecipient : [selectedAccount?.contactEmail] });
        setLoading(false);
        setUploadSuccess(true);
        setNotification({
          type: NOTIFICATION.SUCCESS,
          payload: data?.message,
        });
        close();
        setUploadedFile(null);
      } catch (e) {
        if (e.response && e.response.status === 417) {
          setLoading(false);
          const msgType = Object.keys(e.response.data)[0];
          setUploadSuccess(false);
          setNotification({
            type: NOTIFICATION.ERROR,
            payload: `${msgType}: ${e.response.data[msgType]}`,
          });
        } else if (e.response && e.response.status === 412) {
          setLoading(false);
          const msgType = e.response?.data?.message;
          setUploadSuccess(false);
          setNotification({
            type: NOTIFICATION.ERROR,
            payload: msgType,
          });
        } else {
          setLoading(false);
          setUploadSuccess(false);
          setNotification({
            type: NOTIFICATION.ERROR,
            payload: e.response.data ? e.response.data : MESSAGES.FILE_UPLOAD.ERROR,
          });
        }
      } finally {
        setLoading(false);
        multi = false;
      }
    } else {
      try {
        setLoading(true);
        if (uploadedFile && !multi) {
          multi = true;
          const { data: { entity } } = await axiosAuthInstance.get(`${API_END_POINTS.BULK_ITEMS_STORE}`);
          await axiosEmptyInstance.put(entity, await fileReader(uploadedFile), { headers: { 'Content-Type': uploadedFile.type } });
          // eslint-disable-next-line max-len
          const { data: { entity: { fileUrl } } } = await axiosAuthInstance.post(`${API_END_POINTS.BULK_ITEMS_STORE}`, { fileUrl: entity, keyType: 'BULK_IMPORT_INVENTORY' });
          // eslint-disable-next-line max-len
          const { data } = await axiosAuthInstance.post(`${API_END_POINTS.BULK_ITEMS_ADD}`, { fileUrl, recipientEmails: addRecipient?.length > 0 ? addRecipient : [selectedAccount?.contactEmail] });
          setLoading(false);
          setUploadSuccess(true);
          setNotification({
            type: NOTIFICATION.SUCCESS,
            payload: data?.message,
          });
          close();
          setUploadedFile(null);
          multi = false;
        }
      } catch (e) {
        if (e.response && e.response.status === 417) {
          setLoading(false);
          const msgType = Object.keys(e.response.data)[0];
          setUploadSuccess(false);
          setNotification({
            type: NOTIFICATION.ERROR,
            payload: `${msgType}: ${e.response.data[msgType]}`,
          });
        } else if (e.response && e.response.status === 412) {
          setLoading(false);
          const msgType = e.response?.data?.message;
          setUploadSuccess(false);
          setNotification({
            type: NOTIFICATION.ERROR,
            payload: msgType,
          });
        } else {
          setLoading(false);
          setUploadSuccess(false);
          setNotification({
            type: NOTIFICATION.ERROR,
            payload: e.response.data ? e.response.data : MESSAGES.FILE_UPLOAD.ERROR,
          });
        }
      } finally {
        setLoading(false);
        multi = false;
      }
    }
  };

  const uploadTypeChange = (e) => {
    setUploadType(e.target.value);
  };

  return (
    <Modal
      centered
      closable={false}
      width="600px"
      visible={visible}
      footer={
        uploadType === 'export' ? [
          <SpButton disabled={loading} onClick={() => { close(); setUploadedFile(null); }} type="secondary">
            Cancel
          </SpButton>,
        ] : [
          <SpButton disabled={loading} onClick={() => { close(); setUploadedFile(null); }} type="secondary">
            Cancel
          </SpButton>,
          <SpButton
            onClick={() => storeFile()}
            disabled={!uploadSuccess}
            loading={loading}
          >
            Done
          </SpButton>,
        ]
      }
      title="Bulk Import / Export"
    >
      <>
        <div className="d-flex align-items-center gap-3 ml-3 mb-3 mt-3">
          <SpH5 className="mr-5">Choose Data Mode</SpH5>
          <Radio.Group onChange={uploadTypeChange} value={uploadType}>
            <Radio value="import">Import</Radio>
            <Radio value="export">Export</Radio>
          </Radio.Group>
        </div>
        {
          uploadType === 'export' ? (
            <>
              {
                ((addRecipient.length) === 0) ? (
                  <>
                    <SpForm>
                      <Row type="flex" justify="start" align="top">
                        <Col className="ml-3" span={16} style={{ cursor: 'pointer' }}>
                          <SpText
                            fontWeight="600"
                            color="#279dfe"
                            onClick={addNewRecipient}
                          >
                            + Add New Recipient
                          </SpText>
                        </Col>
                      </Row>
                    </SpForm>
                  </>
                ) : (
                  <>
                    <SpForm>
                      {addRecipient.map((item, i) => (
                        <>
                          <Row type="flex" justify="start" align="top" className="mb-3 ml-3">
                            <Col span={12}>
                              <Input
                                value={item}
                                key={`Email${i}`}
                                onChange={(e) => {
                                  const list = [...addRecipient];
                                  list[i] = e.currentTarget.value;
                                  setAddRecipient(list);
                                }}
                                placeholder="Enter recipient name"
                              />
                              <SpError className="mb-4">
                                {simpleValidator.current.message('email', item, 'required|email|checkLowerCase',
                                  { messages: { email: 'The email must be valid' } })}
                              </SpError>
                            </Col>
                            <Col span={1}>
                              <Icon
                                type="minus-circle"
                                style={{
                                  cursor: 'pointer', color: '#ff4d50', marginLeft: '5px',
                                }}
                                onClick={() => handleRemoveEmail(i)}
                              />
                            </Col>
                          </Row>
                        </>
                      ))}
                    </SpForm>
                    <SpForm>
                      <Row type="flex" justify="start" align="top">
                        <Col className="ml-3" span={16} style={{ cursor: 'pointer' }}>
                          <SpText
                            fontWeight="600"
                            color="#279dfe"
                            onClick={addNewRecipient}
                          >
                            + Add New Recipient
                          </SpText>
                        </Col>
                      </Row>
                    </SpForm>
                  </>
                )
              }
              <div className="d-flex align-items-center gap-3 ml-3">
                <SpH5>Export .xlsx file</SpH5>
                {/* <Upload
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={fileUpload}
                > */}
                <SpButton
                  shape="round"
                  ghost
                  className="ml-3"
                  onClick={() => storeFile()}
                >
                  Export
                </SpButton>
                {/* </Upload> */}
                {uploadLoading && <span className="spinner-border text-muted mt-2 ml-2"> </span>}
                {uploadSuccess && (
                  <div className="d-flex align-items-center ml-3">
                    <Icon type="check-circle" className="mr-1 text-success" style={{ fontSize: '18px' }} />
                    <span className="text-success" style={{ fontSize: '13px' }}>File Uploaded</span>
                  </div>
                )}
              </div>
              {/* <span className="mt-1" style={{ color: '#999999', fontSize: '12px', marginLeft: '8rem' }}>
                File size should be within 2 MB
              </span> */}
              <div>
                <span
                  className="mt-1"
                  style={{
                    color: '#999999', fontSize: '14px', marginLeft: '1rem', fontWeight: '600',
                  }}
                >
                  You will receive an email after the data export is complete.
                </span>
              </div>
            </>
          ) : (
            <>
              <Row
                type="flex"
                justify="space-between"
                style={{
                  border: '1px solid #CBCBCB',
                  marginTop: '10px',
                  marginBottom: '25px',
                  margin: '2rem',
                }}
              >
                <Col
                  span={8}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#fff',
                    borderRight: '1px solid #CBCBCB',
                    cursor: 'pointer',
                  }}
                  onClick={() => downloadTemplate(true)}
                >
                  <img src={File} alt="file" style={{ marginRight: '8px' }} />
                  <span>Download Template</span>
                  <Icon
                    type="download"
                    style={{ color: 'rgba(0, 0, 0, 0.8)', marginTop: '4px', fontSize: '22px' }}
                  />
                </Col>
                <Col span={16} className="p-2">
                  <div className="ml-2">
                    <SpH5>Template</SpH5>
                    <ol>
                      <li>
                        <span className="font-weight-bold">Download the template:</span>{' '}
                        Click the link provided to download the template file to your
                        device.
                      </li>
                      <li>
                        <span className="font-weight-bold">Fill in the details:</span>{' '}
                        Open the template and complete all required fields with your
                        information.
                      </li>
                      <li>
                        <span className="font-weight-bold">
                          Upload the completed file:
                        </span>{' '}
                        Once you’ve filled in the details, save the file and upload it
                        here using the upload button.
                      </li>
                    </ol>
                  </div>
                </Col>
              </Row>
              {
                ((addRecipient.length) === 0) ? (
                  <>
                    <SpForm>
                      <Row type="flex" justify="start" align="top">
                        <Col className="ml-3" span={16} style={{ cursor: 'pointer' }}>
                          <SpText
                            fontWeight="600"
                            color="#279dfe"
                            onClick={addNewRecipient}
                          >
                            + Add New Recipient
                          </SpText>
                        </Col>
                      </Row>
                    </SpForm>
                  </>
                ) : (
                  <>
                    <SpForm>
                      {addRecipient.map((item, i) => (
                        <>
                          <Row type="flex" justify="start" align="top" className="mb-3 ml-3">
                            <Col span={12}>
                              <Input
                                value={item}
                                key={`Email${i}`}
                                onChange={(e) => {
                                  const list = [...addRecipient];
                                  list[i] = e.currentTarget.value;
                                  setAddRecipient(list);
                                }}
                                placeholder="Enter recipient name"
                              />
                              <SpError className="mb-4">
                                {simpleValidator.current.message('email', item, 'required|email|checkLowerCase',
                                  { messages: { email: 'The email must be valid' } })}
                              </SpError>
                            </Col>
                            <Col span={1}>
                              <Icon
                                type="minus-circle"
                                style={{
                                  cursor: 'pointer', color: '#ff4d50', marginLeft: '5px',
                                }}
                                onClick={() => handleRemoveEmail(i)}
                              />
                            </Col>
                          </Row>
                        </>
                      ))}
                    </SpForm>
                    <SpForm>
                      <Row type="flex" justify="start" align="top">
                        <Col className="ml-3" span={16} style={{ cursor: 'pointer' }}>
                          <SpText
                            fontWeight="600"
                            color="#279dfe"
                            onClick={addNewRecipient}
                          >
                            + Add New Recipient
                          </SpText>
                        </Col>
                      </Row>
                    </SpForm>
                  </>
                )
              }
              <div className="d-flex align-items-center gap-3 ml-3">
                <SpH5>Upload .xlsx file</SpH5>
                <Upload
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={fileUpload}
                >
                  <SpButton
                    shape="round"
                    ghost
                    className="ml-3"
                  >
                    <Icon
                      type="upload"
                      style={{ color: 'rgba(0, 0, 0, 0.8)' }}
                    /> Upload
                  </SpButton>
                </Upload>
                {uploadLoading && <span className="spinner-border text-muted mt-2 ml-2"> </span>}
                {uploadSuccess && (
                  <div className="d-flex align-items-center ml-3">
                    <Icon type="check-circle" className="mr-1 text-success" style={{ fontSize: '18px' }} />
                    <span className="text-success" style={{ fontSize: '13px' }}>File Uploaded</span>
                  </div>
                )}
              </div>
              <span className="mt-1" style={{ color: '#999999', fontSize: '12px', marginLeft: '8rem' }}>
                File size should be within 2 MB
              </span>
              <div>
                <span
                  className="mt-1"
                  style={{
                    color: '#999999', fontSize: '14px', marginLeft: '1rem', fontWeight: '600',
                  }}
                >
                  You will receive an email after the data import is complete.
                </span>
              </div>
            </>
          )
        }
      </>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  validator: state.loading.validator,
  submitting: state.loading.submitting,
  test: state.account.test,
});

const mapDispatchToProps = (dispatch) => ({
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(BulkUploadItems);
